import { 
    useState,    
    useEffect, 
} from 'react';
import { 
    useMediaQuery,
    useTheme,
    Box,
    TextField,
    IconButton,
} from '@mui/material';
import { SendRounded, ClearRounded } from '@mui/icons-material';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { useSnackbar } from 'notistack';
import { withPather } from 'react-pather';
import HoverRating from './HoverRating';
import Loader from '../Loader';
import { 
    UUT, 
    UUTLC,
    PROJECT_NAME,
} from '../../config';
import { setToken } from '../../slices/tokenSlice';

const DEFAULR_MESSAGE = '';
const DEFAULT_RATING = 0;

const Feedback = ({ pather }) => {

    const theme = useTheme();
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    const matchesDown400px = useMediaQuery(theme.breakpoints.down(400));

    // Feedback
    const [message, setMessage] = useState(DEFAULR_MESSAGE);
    const [rating, setRating] = useState(DEFAULT_RATING);

    // Feedback Controls
    const [extendMessage, setExtendMessage] = useState(false);
    const [isFeedbackGiven, setIsFeedbackGiven] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isMessageFieldFocused, setIsMessageFieldFocused] = useState(false);

    useEffect(() => {
        if (isFeedbackGiven) {
            setMessage(DEFAULR_MESSAGE);
            setRating(DEFAULT_RATING);
            setExtendMessage(false);
        }
    }, [isFeedbackGiven]);

    const sendFeedback = () => {
        setLoading(true);
        axios.post(
            pather.back.feedback, 
            { 
                message,
                rating, 
            },
        )
        .then(response => {
            const uut = localStorage.getItem(UUTLC);
            if(!uut){
                dispatch(setToken(response.data.token));
                axios.defaults.headers.common[UUT] = response.data.token;
            }
            setIsFeedbackGiven(true);
        })
        .catch(error => {
            console.log(error);
            const status = error?.response?.data?.status;
            if(!!status) enqueueSnackbar(status, { variant: 'error' });
        })
        .finally(() => {
            setLoading(false);
        })
    }
    
    const extendFeedback = message || rating || extendMessage;

    const maxWidthFeedback = matchesDown400px ? '90%' : (isFeedbackGiven ?  '300px' : (extendFeedback ? '350px' : '270px'));
    
    return (
        <Box 
            sx={{
                mb: extendFeedback ? 3 : 1.5,
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '100px', 
                flexDirection: 'column',
                transition: 'all 0.3s ease',
            }}
        >
            {
                loading ?
                <Loader/> :
                <Box
                    sx={{
                        maxWidth: maxWidthFeedback,
                        width: '100%',
                        px: 2,
                        pt: isFeedbackGiven ? 1.75 : (extendFeedback ? 2.5 : 2),
                        pb: 1.5,
                        boxShadow: theme.palette.squareBoxShadow,
                        zIndex: 1,
                        borderRadius: '25px',
                        border: theme.palette.tinyBorder,
                        transition: 'all 0.3s ease',
                    }}
                    onMouseEnter={() => !isFeedbackGiven && setExtendMessage(true)}
                    onMouseLeave={() => {
                        if (message || rating || isMessageFieldFocused || isFeedbackGiven) return;
                        setExtendMessage(false);
                    }}
                >
                    {   
                        isFeedbackGiven ?
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                        }}>
                            <Box>
                                Thank you for your feedback!
                            </Box>
                            <IconButton 
                                sx={{ml: 'auto'}}
                                color="primary" 
                                aria-label="clear feedback"
                                onClick={() => setIsFeedbackGiven(false)}
                                size="small"
                            >
                                <ClearRounded />
                            </IconButton>
                        </Box> :
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                        }}>
                            <TextField 
                                id="feedback-message" 
                                variant="standard"
                                multiline
                                minRows={1}
                                maxRows={4}
                                placeholder={`What do you think about ${PROJECT_NAME}?`}
                                fullWidth
                                value={message}
                                onChange={e => setMessage(e.target.value)}
                                sx={{
                                    borderRadius: '25px',
                                }}
                                onFocus={() => setIsMessageFieldFocused(true)}
                                onBlur={() => setIsMessageFieldFocused(false)}
                                InputProps={{ 
                                    disableUnderline: !extendFeedback,
                                }}
                            />
                            {
                                extendFeedback &&
                                <Box sx={{
                                    display: 'flex',
                                }}>
                                    <HoverRating 
                                        rating={rating} 
                                        setRating={setRating} 
                                        showRatingDescription={!matchesDown400px}
                                    />
                                    <IconButton 
                                        sx={{ml: 'auto'}}
                                        color="primary" 
                                        aria-label="send feedback"
                                        onClick={sendFeedback}
                                    >
                                        <SendRounded />
                                    </IconButton>
                                </Box>
                            }
                        </Box>
                    }
                </Box>
            }
        </Box>
    );
}

export default withPather()(Feedback);